import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Card, ChangeChip, TextLink } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { KpiCardLoading, KpiCardQueryHeader, KpiCardStatusBody } from "app/components/kpi-card/KpiCardChrome";
import { percentageSignFilter } from "app/filters/ngFilters";

import type { IInsightRibbonState, INotableChange } from "./types";

const CHANGE_DECIMALS = 0;

const CHANGE_CHIP_HEIGHT = "24px";

const ItemIndex = styled(Text)`
    min-width: 14px;
    display: flex;
    align-items: center;
    height: ${CHANGE_CHIP_HEIGHT};
`;

const formatChange = ({ changeRatio, formattedChange }: INotableChange): string =>
    formattedChange ?? percentageSignFilter()(Math.abs(changeRatio), CHANGE_DECIMALS);

interface INotableChangeRowProps {
    change: INotableChange;
    index: number;
    onClick: (change: INotableChange) => void;
}

const NotableChangeRow = ({ change, index, onClick }: INotableChangeRowProps) => {
    const { translate } = useTranslate();
    const isDecrease = change.changeRatio < 0;

    return (
        <Flex
            gap="4px"
            data-automation={`insight-ribbon-notable-item-${index}`}
        >
            <ItemIndex
                fontVariant="body2"
                color={colorsPalettes.carbon[400]}
            >
                {`${index + 1}.`}
            </ItemIndex>
            <Stack gap="4px">
                <Flex
                    gap="4px"
                    alignItems="center"
                    flexWrap="wrap"
                >
                    {change.countryCode ? (
                        <SWReactCountryIcons
                            countryCode={change.countryCode}
                            size="xs"
                        />
                    ) : null}
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {change.label}
                    </Text>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate(
                            isDecrease
                                ? "insight_ribbon.shared.notable_changes_card.decreased_by"
                                : "insight_ribbon.shared.notable_changes_card.increased_by",
                        )}
                    </Text>
                    <ChangeChip
                        sentiment={isDecrease ? "negative" : "positive"}
                        size="small"
                        data-automation={`insight-ribbon-notable-change-${index}`}
                    >
                        {formatChange(change)}
                    </ChangeChip>
                </Flex>
                <Flex
                    gap="4px"
                    alignItems="center"
                    flexWrap="wrap"
                >
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("insight_ribbon.shared.notable_changes_card.period")}
                    </Text>
                    <TextLink
                        href={change.deepLink.href}
                        translations={{ opensInNewTab: "" }}
                        onClick={() => onClick(change)}
                        data-automation={`insight-ribbon-notable-link-${index}`}
                    >
                        {translate(change.deepLink.textKey)}
                    </TextLink>
                </Flex>
            </Stack>
        </Flex>
    );
};

export const NotableChangesCard = ({
    notableChanges,
    isLoading,
    isComparisonUnavailable,
    hasError,
    dateRangeText,
    countryText,
    countryCode,
    onNotableChangeClick,
}: IInsightRibbonState) => {
    const { translate } = useTranslate();

    if (isLoading) {
        return (
            <KpiCardLoading
                dataAutomation="insight-ribbon-notable-changes-skeleton"
                bodyVariant="lines"
            />
        );
    }

    const emptyStateKey = isComparisonUnavailable
        ? "insight_ribbon.shared.notable_changes_card.comparison_unavailable"
        : "insight_ribbon.shared.notable_changes_card.empty_state";

    const header = (
        <KpiCardQueryHeader
            title={translate("insight_ribbon.shared.notable_changes_card.title")}
            tooltip={translate("insight_ribbon.shared.notable_changes_card.tooltip")}
            dateRangeText={dateRangeText}
            countryCode={countryCode}
            countryText={countryText}
            selectorTriggerId="insight-ribbon-notable-changes"
            dataAutomationPrefix="insight-ribbon-notable-changes"
        />
    );

    if (hasError && !notableChanges.length) {
        return (
            <Card data-automation="insight-ribbon-notable-changes">
                {header}
                <KpiCardStatusBody
                    title="global.error.title"
                    dataAutomation="insight-ribbon-notable-changes-error"
                />
            </Card>
        );
    }

    return (
        <Card data-automation="insight-ribbon-notable-changes">
            {header}
            <Stack
                padding="24px"
                gap="16px"
                flex="1"
            >
                {notableChanges.length ? (
                    notableChanges.map((change, index) => (
                        <NotableChangeRow
                            key={change.id}
                            change={change}
                            index={index}
                            onClick={onNotableChangeClick}
                        />
                    ))
                ) : (
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        data-automation="insight-ribbon-notable-changes-empty"
                    >
                        {translate(emptyStateKey)}
                    </Text>
                )}
            </Stack>
        </Card>
    );
};
